/**
 * Fetch a font's raw bytes from the public whitelist and install it
 * into `FontRegistry` + the persistent `FontStore`.
 *
 * This is the programmatic entry point for the "Find online" action in
 * `FontManagerDialog`: the user clicks the button next to a missing
 * font name, the dialog hands the name + the module-level store to
 * `fetchFontFromWhitelist`, and the returned `WebFetchResult` drives
 * the status line (localised) and the missing-list drain.
 *
 * ### Discriminated result
 *
 * All failure modes are enumerated on the `reason` field so the caller
 * picks a translated message from a map rather than parsing exception
 * strings. The shape mirrors the project-wide pattern (see
 * `ShxParseResult`, `TtfLoadResult`).
 *
 * ### Idempotency
 *
 * Calling `fetchFontFromWhitelist("simplex", …)` twice is safe: the
 * second call short-circuits with `reason: "already-loaded"` because
 * `FontRegistry.has` already resolves. This mirrors HTTP-caching
 * semantics and avoids redundant network traffic.
 *
 * ### Abort
 *
 * An optional `AbortSignal` is forwarded to both `fetch` and
 * `store.save`. On abort the function returns `reason: "aborted"`
 * rather than throwing — keeping the caller's control flow flat (no
 * try/catch around await) and matching the `FontStore` contract.
 */

import { FontRegistry, type RegisterResult } from "./fontRegistry";
import type { FontStore } from "./fontStore";
import { lookupWhitelistUrl } from "./whitelist";

/**
 * Result of a single whitelist fetch attempt.
 *
 * The `reason` values cover the six observable outcomes:
 *   - `"not-in-whitelist"` — we have no known URL for this name
 *   - `"network"`          — HTTP response was non-2xx
 *   - `"cors"`             — `fetch` rejected with a `TypeError`
 *                             (typically a network-level CORS failure)
 *   - `"parse"`            — bytes returned were not a recognised
 *                             SHX / TTF file
 *   - `"already-loaded"`   — `FontRegistry.has(name) === true` before
 *                             the fetch was attempted
 *   - `"aborted"`          — the `AbortSignal` fired during fetch or
 *                             persistence
 *
 * On success, `fontName` is the CANONICAL name the registry stored the
 * font under (from the SHX header or TTF family), which may differ in
 * case from the DXF reference name the caller passed in.
 */
export type WebFetchResult =
  | { ok: true; fontName: string }
  | {
      ok: false;
      reason:
        | "not-in-whitelist"
        | "network"
        | "cors"
        | "parse"
        | "already-loaded"
        | "aborted";
      message: string;
      /** HTTP status code on `reason: "network"`, otherwise undefined. */
      httpStatus?: number;
    };

/**
 * Attempt to fetch a font by its DXF STYLE name from the public
 * whitelist, register it in `FontRegistry`, and persist to the
 * `FontStore`.
 *
 * Signature notes:
 * - `store` is nullable because the application falls back to
 *   in-memory-only mode when `IndexedDbFontStore` construction fails
 *   (private browsing, CSP, unsupported UA). Passing `null` skips
 *   persistence silently; the font still ends up in `FontRegistry`.
 * - Persistence errors are swallowed (logged to `console.warn`). The
 *   user-facing result still reports success because the font IS
 *   usable for the current session — it just won't replay after
 *   reload.
 */
export async function fetchFontFromWhitelist(
  fontName: string,
  store: FontStore | null,
  signal?: AbortSignal,
): Promise<WebFetchResult> {
  // Short-circuit: registry already has the font. We intentionally
  // check BEFORE consulting the whitelist so the caller sees a stable
  // "already-loaded" outcome even for names that aren't listed (e.g.
  // a custom SHX uploaded manually during this session).
  if (FontRegistry.has(fontName)) {
    return {
      ok: false,
      reason: "already-loaded",
      message: "font already registered",
    };
  }

  const url = lookupWhitelistUrl(fontName);
  if (url === undefined) {
    return {
      ok: false,
      reason: "not-in-whitelist",
      message: `No public URL known for "${fontName}"`,
    };
  }

  // -----------------------------------------------------------------
  // Network step — download the bytes.
  //
  // `fetch` rejects with a `TypeError` on network-level failures
  // (CORS, DNS, offline). Non-2xx responses resolve but have
  // `resp.ok === false`, so we split the two paths.
  // -----------------------------------------------------------------
  let bytes: Uint8Array;
  try {
    // `exactOptionalPropertyTypes: true` in the project tsconfig rejects
    // passing `signal: undefined` because `RequestInit.signal` is typed
    // `AbortSignal | null`. Build the init object conditionally so the
    // signal key is entirely absent when the caller didn't supply one.
    const init: RequestInit = signal !== undefined ? { signal } : {};
    const resp = await fetch(url, init);
    if (!resp.ok) {
      return {
        ok: false,
        reason: "network",
        message: `HTTP ${resp.status} ${resp.statusText}`,
        httpStatus: resp.status,
      };
    }
    bytes = new Uint8Array(await resp.arrayBuffer());
  } catch (error) {
    // AbortError comes through as a `DOMException` (both in browsers
    // and in Node's undici-based fetch). Check by name so we don't
    // need a reference to the constructor which varies by runtime.
    if ((error as { name?: string })?.name === "AbortError") {
      return {
        ok: false,
        reason: "aborted",
        message: "Fetch aborted",
      };
    }
    // TypeError from `fetch()` almost always means a network-level
    // failure (CORS, DNS, offline). We surface it as "cors" because
    // that's the overwhelmingly common case for whitelist sources —
    // a CORS header missing on the upstream blob. `message` carries
    // the raw details for debug logs.
    if (error instanceof TypeError) {
      return {
        ok: false,
        reason: "cors",
        message: error.message || "Network/CORS failure",
      };
    }
    return {
      ok: false,
      reason: "network",
      message: error instanceof Error ? error.message : String(error),
    };
  }

  // -----------------------------------------------------------------
  // Parse step — `FontRegistry.register` detects SHX vs TTF and
  // handles both. We pass the caller's DXF reference name as the
  // hint so a synthetic filename is not used when the header itself
  // doesn't supply one.
  // -----------------------------------------------------------------
  const parse: RegisterResult = await FontRegistry.register(bytes, fontName);
  if (!parse.ok) {
    return {
      ok: false,
      reason: "parse",
      message: parse.error.message,
    };
  }

  // -----------------------------------------------------------------
  // Persistence step — best-effort. The font is already usable in the
  // registry, so we don't fail the whole operation if IndexedDB is
  // full / blocked / quota-exceeded. The warning keeps the failure
  // visible in the console for debug.
  //
  // Note: we persist under the PARSED canonical name (from the SHX
  // header or TTF family), NOT the DXF reference name. This matches
  // the upload flow in `FontManagerDialog.processFiles` so both code
  // paths produce a consistent IndexedDB key layout.
  // -----------------------------------------------------------------
  if (store !== null) {
    try {
      await store.save(parse.entry.name, bytes, signal);
    } catch (error) {
      // A failed persist is logged but does NOT propagate — the font
      // stays in-memory for this session.
      console.warn("[webFontFetcher] persist failed:", error);
    }
  }

  return { ok: true, fontName: parse.entry.name };
}
